import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { contactsAPI } from '../services/api';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiX, FiUser, FiPhone, FiMail } from 'react-icons/fi';
import OrgBranchPicker from '../components/contacts/OrgBranchPicker';
import softphone from '../services/softphone';
import toast from 'react-hot-toast';
import { formatDateTime } from '../utils/formatters';

export default function ContactsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);

  const isAdmin = user?.role === 'admin';

  const loadContacts = async () => {
    try {
      setLoading(true);
      const res = await contactsAPI.list({ search, limit: 200 });
      setContacts(res.data.contacts || []);
    } catch (err) {
      toast.error("Kontaktlarni olishda xato");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(loadContacts, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const openCreateModal = () => {
    setEditingContact(null);
    setModalOpen(true);
  };

  const openEditModal = (contact) => {
    setEditingContact(contact);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingContact(null);
  };

  const handleSave = async (data) => {
    try {
      if (editingContact) {
        await contactsAPI.update(editingContact.id, data);
        toast.success("Yangilandi");
      } else {
        await contactsAPI.create(data);
        toast.success("Qo'shildi");
      }
      closeModal();
      loadContacts();
    } catch (err) {
      toast.error(err.response?.data?.error || "Xatolik");
    }
  };

  const handleDelete = async (contact) => {
    if (!window.confirm(`"${contact.name}" kontaktini o'chirmoqchimisiz?`)) return;
    try {
      await contactsAPI.delete(contact.id);
      toast.success("O'chirildi");
      loadContacts();
    } catch (err) {
      toast.error(err.response?.data?.error || "O'chirishda xato");
    }
  };

  const canEdit = (contact) => isAdmin || contact.created_by === user?.id;
  
  const handleCall = (phone) => {
    if (!phone) return;
    softphone.triggerCall(phone);
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Kontaktlar</h2>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
              Jami: {contacts.length} ta kontakt
            </p>
          </div>
          <button className="btn btn-primary" onClick={openCreateModal}>
            <FiPlus size={16} /> Yangi kontakt
          </button>
        </div>

        <div style={{ marginTop: 16, position: 'relative' }}>
          <FiSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            type="text"
            placeholder="Ism, raqam, kompaniya bo'yicha qidirish..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px 10px 40px',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              color: 'var(--text-primary)',
              fontSize: 14,
              outline: 'none',
            }}
          />
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : contacts.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)' }}>
            {search ? "Hech narsa topilmadi" : "Hali kontaktlar yo'q. Birinchisini qo'shing!"}
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Ism</th>
                  <th>Raqam</th>
                  <th>Kompaniya</th>
                  <th>Email</th>
                  {isAdmin && <th>Yaratgan</th>}
                  <th>Yangilangan</th>
                  <th style={{ width: 100 }}>Amallar</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map(c => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 600 }}>
                      {isAdmin ? (
                        <span onClick={() => navigate(`/admin/contacts/${c.id}`)}
                          style={{ cursor: 'pointer', color: 'var(--accent, #3b82f6)' }}
                          title="Profilni ochish">{c.name}</span>
                      ) : c.name}
                    </td>
                    <td 
                      style={{ fontFamily: 'monospace', fontWeight: 600, cursor: 'pointer', color: 'var(--accent, #3b82f6)' }}
                      onClick={() => handleCall(c.phone)}
                      title="Qo'ng'iroq qilish"
                      className="clickable-number"
                    >{c.phone}</td>
                    <td>
                      {c.org_name || c.company || '—'}
                      {c.branch_name && (
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.branch_name}</div>
                      )}
                    </td>
                    <td>{c.email || '—'}</td>
                    {isAdmin && <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.created_by_name || '—'}</td>}
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{formatDateTime(c.updated_at)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {canEdit(c) && (
                          <>
                            <button className="btn btn-ghost btn-icon" onClick={() => openEditModal(c)} title="Tahrirlash">
                              <FiEdit2 size={14} />
                            </button>
                            <button className="btn btn-ghost btn-icon" onClick={() => handleDelete(c)} title="O'chirish" style={{ color: '#ef4444' }}>
                              <FiTrash2 size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalOpen && (
        <ContactModal
          contact={editingContact}
          onClose={closeModal}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

// Sinovda alohida render qilinadi (oyna ichidagi xato kompilyatsiyada ko'rinmaydi).
export function ContactModal({ contact, onClose, onSave }) {
  const [form, setForm] = useState({
    phone: contact?.phone || '',
    name: contact?.name || '',
    email: contact?.email || '',
    // Eski, qo'lda yozilgan nom: tashkilot tanlanmasa shu saqlanib qoladi.
    company: contact?.company || '',
    notes: contact?.notes || '',
    org_ref: contact?.org_ref || '',
    org_name: contact?.org_name || '',
    org_inn: contact?.org_inn || '',
    branch_userid: contact?.branch_userid || '',
    branch_name: contact?.branch_name || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.phone.trim() || !form.name.trim()) {
      return;
    }
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
            {contact ? 'Kontaktni tahrirlash' : 'Yangi kontakt'}
          </h3>
          <button onClick={onClose} style={styles.closeBtn}><FiX size={18} /></button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label style={styles.label}><FiUser size={14} /> Ism *</label>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              style={styles.input}
              placeholder="Ism Familiya"
              autoFocus
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}><FiPhone size={14} /> Telefon raqam *</label>
            <input
              type="text"
              required
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              style={styles.input}
              placeholder="998901234567"
            />
          </div>

          <div style={styles.formGroup}>
            <OrgBranchPicker
              value={form}
              onChange={(next) => setForm({ ...form, ...next })}
              legacyCompany={contact?.org_ref ? '' : contact?.company}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}><FiMail size={14} /> Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              style={styles.input}
              placeholder="Ixtiyoriy"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Izoh</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              style={{ ...styles.input, minHeight: 80, resize: 'vertical' }}
              placeholder="Qo'shimcha ma'lumot"
            />
          </div>

          <div style={styles.modalFooter}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Bekor qilish
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saqlanmoqda..." : contact ? "Yangilash" : "Qo'shish"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, backdropFilter: 'blur(4px)',
  },
  modal: {
    background: 'var(--bg-secondary, #1e293b)',
    border: '1px solid var(--border, #2d3a4f)',
    borderRadius: 16, padding: 24, width: '100%', maxWidth: 480,
    maxHeight: '90vh', overflow: 'auto',
  },
  modalHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 20,
  },
  closeBtn: {
    background: 'transparent', border: 'none', color: 'var(--text-muted)',
    cursor: 'pointer', padding: 4,
  },
  formGroup: { marginBottom: 16 },
  label: {
    display: 'flex', alignItems: 'center', gap: 6,
    fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6,
  },
  input: {
    width: '100%', padding: '10px 12px',
    background: 'var(--bg-primary, #0f172a)',
    border: '1px solid var(--border, #2d3a4f)',
    borderRadius: 8, color: 'var(--text-primary, #f1f5f9)',
    fontSize: 14, outline: 'none', fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  modalFooter: {
    display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 24,
  },
};
